import React, { useState, useEffect, useRef } from 'react';
import { consumeStampPaper, mergePdfs, base64ToUint8Array } from '../../services/demoApi';

const PHASES = [
  { key: 'consuming', text: 'Consuming Stamp Paper...', sub: 'Fetching next available document from bulk order' },
  { key: 'merging', text: 'Merging Documents...', sub: 'Stamp Paper (Page 1) + Loan Agreement → Single PDF' },
];

const Step3Docustamp = ({ formData, onNext, demoState, setDemoState }) => {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [stampMeta, setStampMeta] = useState(null);
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (hasCalledApi.current) return;
    hasCalledApi.current = true;

    const run = async () => {
      try {
        // Phase 1: Consume stamp paper from bulk order
        setPhaseIndex(0);
        const consumeResult = await consumeStampPaper();

        const stampBytes = base64ToUint8Array(consumeResult.file_base64);
        setStampMeta(consumeResult.metadata);

        setDemoState(prev => ({
          ...prev,
          stampDocumentId: consumeResult.document_id,
          stampCertificateId: consumeResult.metadata?.certificate_id || null,
          stampPdfBytes: stampBytes,
        }));

        // Phase 2: Merge PDFs client-side
        setPhaseIndex(1);
        const agreementBytes = demoState.agreementPdfBytes;
        if (!agreementBytes) {
          throw new Error('Agreement PDF not available. Please go back to Step 2.');
        }

        const mergedBytes = await mergePdfs(stampBytes, new Uint8Array(agreementBytes));

        setDemoState(prev => ({
          ...prev,
          mergedPdfBytes: mergedBytes,
        }));

        setDone(true);
      } catch (err) {
        console.error('DocuStamp step failed:', err);
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        setError(detail);
      }
    };

    run();
  }, [demoState.agreementPdfBytes, setDemoState]);

  // ─── Error State ──────────────────────────────────────────
  if (error) {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number" style={{ background: 'var(--loan-error)' }}>!</div>
          <span className="loan-step-text">Step 3 of 5 — e-Stamp Paper</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--docustamp">
            ✕ DocuStamp API — Error
          </div>
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            Stamp Paper Procurement Failed
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            Could not consume a stamp paper from the bulk order. Ensure the order has available documents.
          </p>
          <div className="loan-doc-preview" style={{ textAlign: 'center', padding: 16 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--loan-error)', wordBreak: 'break-all' }}>
              {error}
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <button
              className="loan-btn loan-btn--primary"
              onClick={() => {
                hasCalledApi.current = false;
                setError(null);
                setPhaseIndex(0);
              }}
            >
              ↻ Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Processing State ─────────────────────────────────────
  if (!done) {
    const current = PHASES[phaseIndex] || PHASES[0];
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">3</div>
          <span className="loan-step-text">Step 3 of 5 — e-Stamp Paper</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--docustamp">
            ⚡ DocuStamp API
          </div>
          <div className="loan-processing">
            <div className="loan-spinner" />
            <div className="loan-processing__text">{current.text}</div>
            <div className="loan-processing__subtext">{current.sub}</div>
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
            {PHASES.map((p, i) => (
              <div
                key={p.key}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: i <= phaseIndex ? 'var(--loan-accent)' : 'var(--loan-bg-progress)',
                  transition: 'background 0.3s',
                }}
              />
            ))}
          </div>
        </div>
      </>
    );
  }

  // ─── Success State ────────────────────────────────────────
  return (
    <>
      <div className="loan-step-label">
        <div className="loan-step-number">3</div>
        <span className="loan-step-text">Step 3 of 5 — e-Stamp Paper</span>
      </div>

      <div className="loan-card">
        <div className="loan-api-tag loan-api-tag--docustamp">
          ✓ DocuStamp API — Complete
        </div>
        <h2 className="loan-heading" style={{ fontSize: 22 }}>
          e-Stamp Attached
        </h2>
        <p className="loan-subheading" style={{ marginBottom: 16 }}>
          A stamp paper has been consumed from your pre-fulfilled bulk order inventory and merged with the Loan Agreement.
        </p>

        {/* e-Stamp Preview */}
        <div className="loan-doc-preview" style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 16 }}>
            <div className="loan-stamp-badge loan-stamp-badge--stamp">
              ✦ e-STAMP CERTIFICATE ATTACHED
            </div>
          </div>

          <div className="loan-doc-field">
            <span className="loan-doc-field__label">Document ID</span>
            <span className="loan-doc-field__value" style={{ fontFamily: 'var(--loan-font-display)', letterSpacing: '0.04em' }}>
              {demoState.stampDocumentId?.slice(0, 16) || 'N/A'}...
            </span>
          </div>
          {stampMeta?.certificate_id && (
            <div className="loan-doc-field">
              <span className="loan-doc-field__label">Certificate ID</span>
              <span className="loan-doc-field__value">{stampMeta.certificate_id}</span>
            </div>
          )}
          <div className="loan-doc-field">
            <span className="loan-doc-field__label">Source</span>
            <span className="loan-doc-field__value">Pre-fulfilled Bulk Order</span>
          </div>
          <div className="loan-doc-field" style={{ borderBottom: 'none' }}>
            <span className="loan-doc-field__label">Merge Status</span>
            <span className="loan-doc-field__value" style={{ color: 'var(--loan-success)' }}>
              ✓ Stamp Paper + Agreement Merged
            </span>
          </div>
        </div>

        <div className="loan-info-row" style={{ marginTop: 16 }}>
          <div className="loan-info-row__icon">📄</div>
          <div className="loan-info-row__content">
            <div className="loan-info-row__title">Document Bundle Ready</div>
            <div className="loan-info-row__subtitle">
              Stamp Paper + Loan Agreement → Merged PDF
            </div>
          </div>
          <div className="loan-badge loan-badge--success">Ready</div>
        </div>

        <div style={{ marginTop: 24 }}>
          <button className="loan-btn loan-btn--primary" onClick={onNext}>
            Proceed to Aadhaar eSign →
          </button>
        </div>
      </div>
    </>
  );
};

export default Step3Docustamp;
